// src/pages/admin/AdminRegion.tsx
//
// Regions and their nested Provinces.
// Under each region card, an expandable section shows all provinces
// with options to add, edit (name/PYP), archive, and restore.

import React, { useMemo, useState } from "react";
import {
  MapPin,
  Plus,
  Search,
  Archive,
  RotateCcw,
  UserPlus,
  Check,
  X,
  Loader2,
  ShieldAlert,
  Users,
  Building2,
  ChevronDown,
  ChevronUp,
  Pencil,
} from "lucide-react";

import { useAppSelector } from "../../app/hooks";
import {
  useGetRegionsQuery,
  useCreateRegionMutation,
  useUpdateRegionMutation,
  useArchiveRegionMutation,
  useUnarchiveRegionMutation,
  useGetRegionRypUsersQuery,
  useGetRegionPypUsersQuery,
  useCreateProvinceMutation,
  useUpdateProvinceMutation,
  useArchiveProvinceMutation,
  useUnarchiveProvinceMutation,
  type RegionRecord,
  type ProvinceRecord,
} from "../../app/api/regionApi";

/* ─────────────────────────────────────────────────────────────────
   ACCESS
───────────────────────────────────────────────────────────────── */

const ALLOWED_ROLES = ["cyp", "admin", "co", "committee_chair"] as const;
type AllowedRole = (typeof ALLOWED_ROLES)[number];

/* ─────────────────────────────────────────────────────────────────
   TYPES
───────────────────────────────────────────────────────────────── */

interface RegionFormState {
  name: string;
  address: string;
}

const EMPTY_REGION_FORM: RegionFormState = { name: "", address: "" };

/* ─────────────────────────────────────────────────────────────────
   PRIMITIVES
───────────────────────────────────────────────────────────────── */

const Bone: React.FC<{ className?: string }> = ({ className = "" }) => (
  <div className={`animate-pulse rounded-xl bg-gray-100 ${className}`} />
);

const Input: React.FC<React.InputHTMLAttributes<HTMLInputElement>> = (props) => (
  <input
    {...props}
    className={`w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm
    focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent
    placeholder-gray-400 transition ${props.className ?? ""}`}
  />
);

const Textarea: React.FC<React.TextareaHTMLAttributes<HTMLTextAreaElement>> = (props) => (
  <textarea
    {...props}
    className={`w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm
    focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent
    placeholder-gray-400 transition resize-none ${props.className ?? ""}`}
  />
);

const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (props) => (
  <select
    {...props}
    className={`w-full rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm
    focus:outline-none focus:ring-2 focus:ring-purple-300 focus:border-transparent
    transition ${props.className ?? ""}`}
  />
);

const FieldLabel: React.FC<{ children: React.ReactNode; required?: boolean }> = ({
  children,
  required,
}) => (
  <label className="mb-1 block text-xs font-semibold text-gray-700">
    {children}
    {required && <span className="ml-0.5 text-red-500">*</span>}
  </label>
);

const InlineError: React.FC<{ msg: string }> = ({ msg }) => (
  <div className="flex items-center gap-2 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-xs text-red-600">
    <ShieldAlert size={13} className="shrink-0" />
    {msg}
  </div>
);

/* ─────────────────────────────────────────────────────────────────
   CREATE REGION MODAL
───────────────────────────────────────────────────────────────── */

const CreateRegionModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (values: RegionFormState) => Promise<void>;
  isSaving: boolean;
  apiError: string | null;
}> = ({ isOpen, onClose, onSubmit, isSaving, apiError }) => {
  const [form, setForm] = useState<RegionFormState>(EMPTY_REGION_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});

  React.useEffect(() => {
    if (isOpen) { setForm(EMPTY_REGION_FORM); setErrors({}); }
  }, [isOpen]);

  if (!isOpen) return null;

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = "Region name is required.";
    if (!form.address.trim()) next.address = "Region address is required.";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    await onSubmit({ name: form.name.trim(), address: form.address.trim() });
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-base font-bold text-gray-900">Create region</h2>
            <p className="mt-0.5 text-xs text-gray-400">
              Add a new region. Provinces can be added after creation.
            </p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-6">
          <div>
            <FieldLabel required>Name</FieldLabel>
            <Input
              placeholder="e.g. Lagos Province"
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />
            {errors.name && <p className="mt-1 text-[11px] text-red-500">{errors.name}</p>}
          </div>

          <div>
            <FieldLabel required>Address</FieldLabel>
            <Textarea
              rows={4}
              placeholder="Region office address..."
              value={form.address}
              onChange={(e) => setForm((p) => ({ ...p, address: e.target.value }))}
            />
            {errors.address && <p className="mt-1 text-[11px] text-red-500">{errors.address}</p>}
          </div>

          {apiError && <InlineError msg={apiError} />}

          <div className="flex items-center justify-end gap-3 border-t pt-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="inline-flex items-center gap-2 rounded-xl bg-purple-600 px-5 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {isSaving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : <><Check size={14} /> Create region</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   ASSIGN RYP MODAL
───────────────────────────────────────────────────────────────── */

const AssignRypModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  regionId: string | null;
  regionName: string;
  currentRypId?: string;
  rypOptions: Array<{ id: string; firstName: string; lastName: string; email: string }>;
  onSubmit: (regionId: string, rypId: string) => Promise<void>;
  isSaving: boolean;
  apiError: string | null;
}> = ({ isOpen, onClose, regionId, regionName, currentRypId, rypOptions, onSubmit, isSaving, apiError }) => {
  const [ryp, setRyp] = useState("");

  React.useEffect(() => {
    if (isOpen) setRyp(currentRypId ?? "");
  }, [isOpen, currentRypId]);

  if (!isOpen || !regionId) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ryp) return;
    await onSubmit(regionId, ryp);
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b px-6 py-4">
          <div>
            <h2 className="text-base font-bold text-gray-900">Assign RYP</h2>
            <p className="mt-0.5 text-xs text-gray-400">{regionName}</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 px-6 py-6">
          <div>
            <FieldLabel required>Regional Youth Pastor (RYP)</FieldLabel>
            <Select value={ryp} onChange={(e) => setRyp(e.target.value)}>
              <option value="">Select an RYP user</option>
              {rypOptions.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.firstName} {user.lastName} — {user.email}
                </option>
              ))}
            </Select>
            <p className="mt-1 text-[11px] text-gray-400">
              Only users with the <strong>ryp</strong> role are shown here.
            </p>
          </div>

          {apiError && <InlineError msg={apiError} />}

          <div className="flex items-center justify-end gap-3 border-t pt-4">
            <button type="button" onClick={onClose} className="rounded-xl border border-gray-200 px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !ryp}
              className="inline-flex items-center gap-2 rounded-xl bg-purple-600 px-5 py-2 text-sm font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {isSaving ? <><Loader2 size={14} className="animate-spin" /> Saving...</> : <><UserPlus size={14} /> Save RYP</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   PROVINCE PANEL (inline inside each region card)
───────────────────────────────────────────────────────────────── */

interface ProvinceFormState {
  name: string;
  pypId: string;
}

const EMPTY_PROVINCE_FORM: ProvinceFormState = { name: "", pypId: "" };

const ProvincePanel: React.FC<{
  region: RegionRecord;
  pypOptions: Array<{ id: string; firstName: string; lastName: string; email: string }>;
}> = ({ region, pypOptions }) => {
  const [showArchived, setShowArchived] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState<ProvinceFormState>(EMPTY_PROVINCE_FORM);
  const [addError, setAddError] = useState<string | null>(null);

  // Edit state — keyed by province id
  const [editId, setEditId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<ProvinceFormState>(EMPTY_PROVINCE_FORM);
  const [editError, setEditError] = useState<string | null>(null);

  const [createProvince, { isLoading: creating }] = useCreateProvinceMutation();
  const [updateProvince, { isLoading: updating }] = useUpdateProvinceMutation();
  const [archiveProvince, { isLoading: archiving }] = useArchiveProvinceMutation();
  const [unarchiveProvince, { isLoading: unarchiving }] = useUnarchiveProvinceMutation();

  // Active provinces come from the region's populated provinces array
  const activeProvinces = (region.provinces ?? []).filter((p) => !p.isArchived);
  // For "show archived" we'd need a separate query; we show what we have
  const visibleProvinces = showArchived
    ? (region.provinces ?? [])
    : activeProvinces;

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setAddError(null);
    if (!addForm.name.trim()) { setAddError("Province name is required."); return; }
    try {
      await createProvince({
        regionId: region.id,
        name: addForm.name.trim(),
        pyp: addForm.pypId || undefined,
      }).unwrap();
      setAddForm(EMPTY_PROVINCE_FORM);
      setAddOpen(false);
    } catch (err: any) {
      setAddError(err?.data?.error ?? "Could not create province.");
    }
  };

  const startEdit = (p: ProvinceRecord) => {
    setEditId(p.id);
    setEditForm({ name: p.name, pypId: p.pyp?.id ?? "" });
    setEditError(null);
  };

  const handleEdit = async (e: React.FormEvent, p: ProvinceRecord) => {
    e.preventDefault();
    setEditError(null);
    try {
      await updateProvince({
        regionId: region.id,
        provinceId: p.id,
        name: editForm.name.trim() || undefined,
        pyp: editForm.pypId || undefined,
      }).unwrap();
      setEditId(null);
    } catch (err: any) {
      setEditError(err?.data?.error ?? "Could not update province.");
    }
  };

  const handleArchive = async (p: ProvinceRecord) => {
    if (!window.confirm(`Archive province "${p.name}"?`)) return;
    try {
      await archiveProvince({ regionId: region.id, provinceId: p.id }).unwrap();
    } catch { /* swallow */ }
  };

  const handleUnarchive = async (p: ProvinceRecord) => {
    try {
      await unarchiveProvince({ regionId: region.id, provinceId: p.id }).unwrap();
    } catch { /* swallow */ }
  };

  return (
    <div className="rounded-xl border border-gray-100 bg-gray-50/60 p-4 space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Building2 size={13} className="text-purple-500" />
          <p className="text-xs font-bold text-gray-700">
            Provinces
            <span className="ml-1.5 text-gray-400 font-normal">({activeProvinces.length} active)</span>
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Show archived toggle */}
          {(region.provinces?.length ?? 0) > activeProvinces.length && (
            <button
              onClick={() => setShowArchived((v) => !v)}
              className="text-[11px] font-semibold text-gray-400 hover:text-gray-600 transition-colors"
            >
              {showArchived ? "Hide archived" : "Show archived"}
            </button>
          )}

          <button
            onClick={() => { setAddOpen((v) => !v); setAddError(null); setAddForm(EMPTY_PROVINCE_FORM); }}
            className="inline-flex items-center gap-1 rounded-lg bg-purple-600 px-2.5 py-1.5 text-[11px] font-semibold text-white hover:bg-purple-700 transition-colors"
          >
            <Plus size={11} />
            Add province
          </button>
        </div>
      </div>

      {/* Add province form */}
      {addOpen && (
        <form onSubmit={handleAdd} className="rounded-xl border border-purple-100 bg-white p-3 space-y-3">
          <p className="text-[11px] font-bold text-gray-700">New province</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <input
              type="text"
              value={addForm.name}
              onChange={(e) => setAddForm((p) => ({ ...p, name: e.target.value }))}
              placeholder="Province name *"
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            />
            <select
              value={addForm.pypId}
              onChange={(e) => setAddForm((p) => ({ ...p, pypId: e.target.value }))}
              className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
            >
              <option value="">Select PYP (optional)</option>
              {pypOptions.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName} — {u.email}
                </option>
              ))}
            </select>
          </div>
          {addError && <InlineError msg={addError} />}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => setAddOpen(false)}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={creating}
              className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
            >
              {creating ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
              Save province
            </button>
          </div>
        </form>
      )}

      {/* Province list */}
      {visibleProvinces.length === 0 ? (
        <p className="text-[11px] text-gray-400 py-2">
          No provinces yet. Add the first one above.
        </p>
      ) : (
        <div className="space-y-2">
          {visibleProvinces.map((province) => (
            <div
              key={province.id}
              className={`rounded-xl border bg-white p-3 transition-all ${
                province.isArchived ? "border-dashed border-amber-200 opacity-60" : "border-gray-100"
              }`}
            >
              {editId === province.id ? (
                /* ── Edit form ── */
                <form onSubmit={(e) => handleEdit(e, province)} className="space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))}
                      placeholder="Province name"
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                    />
                    <select
                      value={editForm.pypId}
                      onChange={(e) => setEditForm((p) => ({ ...p, pypId: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-300"
                    >
                      <option value="">No PYP</option>
                      {pypOptions.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.firstName} {u.lastName}
                        </option>
                      ))}
                    </select>
                  </div>
                  {editError && <InlineError msg={editError} />}
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setEditId(null)}
                      className="rounded-lg border border-gray-200 px-3 py-1 text-[11px] font-semibold text-gray-600 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={updating}
                      className="inline-flex items-center gap-1 rounded-lg bg-purple-600 px-3 py-1 text-[11px] font-semibold text-white hover:bg-purple-700 disabled:opacity-50"
                    >
                      {updating ? <Loader2 size={10} className="animate-spin" /> : <Check size={10} />}
                      Save
                    </button>
                  </div>
                </form>
              ) : (
                /* ── Display row ── */
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {province.name}
                      </p>
                      {province.isArchived && (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">
                          Archived
                        </span>
                      )}
                    </div>
                    {province.pyp ? (
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        PYP: {province.pyp.firstName} {province.pyp.lastName}
                        <span className="text-gray-300 ml-1">· {province.pyp.email}</span>
                      </p>
                    ) : (
                      <p className="text-[11px] text-gray-300 mt-0.5">No PYP assigned</p>
                    )}
                  </div>

                  <div className="flex items-center gap-1.5 shrink-0">
                    {!province.isArchived && (
                      <button
                        onClick={() => startEdit(province)}
                        className="rounded-lg border border-gray-200 p-1.5 text-gray-400 hover:bg-gray-50 hover:text-purple-600 transition-colors"
                        title="Edit province"
                      >
                        <Pencil size={12} />
                      </button>
                    )}

                    {province.isArchived ? (
                      <button
                        onClick={() => handleUnarchive(province)}
                        disabled={unarchiving}
                        className="inline-flex items-center gap-1 rounded-lg bg-green-50 px-2.5 py-1.5 text-[11px] font-semibold text-green-700 hover:bg-green-100 disabled:opacity-50"
                      >
                        <RotateCcw size={11} />
                        Restore
                      </button>
                    ) : (
                      <button
                        onClick={() => handleArchive(province)}
                        disabled={archiving}
                        className="inline-flex items-center gap-1 rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-50"
                      >
                        <Archive size={11} />
                        Archive
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────
   MAIN
───────────────────────────────────────────────────────────────── */

const AdminRegion: React.FC = () => {
  const authUser = useAppSelector((s) => s.auth.user);
  const role = (authUser?.role ?? "") as AllowedRole | string;
  const hasAccess = ALLOWED_ROLES.includes(role as AllowedRole);

  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [expandedRegions, setExpandedRegions] = useState<Set<string>>(new Set());

  const [assignModal, setAssignModal] = useState<{
    open: boolean;
    regionId: string | null;
    regionName: string;
    currentRypId?: string;
  }>({ open: false, regionId: null, regionName: "", currentRypId: undefined });

  const [createError, setCreateError] = useState<string | null>(null);
  const [assignError, setAssignError] = useState<string | null>(null);

  const { data: regionsRes, isLoading: regionsLoading, isFetching: regionsFetching } =
    useGetRegionsQuery({ archived: showArchived });

  const { data: rypUsersRes, isLoading: rypLoading } =
    useGetRegionRypUsersQuery(undefined, { skip: !hasAccess });

  const { data: pypUsersRes } =
    useGetRegionPypUsersQuery(undefined, { skip: !hasAccess });

  const [createRegion, { isLoading: isCreating }] = useCreateRegionMutation();
  const [updateRegion, { isLoading: isUpdatingRegion }] = useUpdateRegionMutation();
  const [archiveRegion, { isLoading: isArchiving }] = useArchiveRegionMutation();
  const [unarchiveRegion, { isLoading: isUnarchiving }] = useUnarchiveRegionMutation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const regions = regionsRes?.data ?? [];

  const rypOptions = useMemo(
    () => (rypUsersRes?.data ?? []).map((u) => ({ id: u.id, firstName: u.firstName, lastName: u.lastName, email: u.email })),
    [rypUsersRes],
  );

  const pypOptions = useMemo(
    () => (pypUsersRes?.data ?? []).map((u) => ({ id: u.id, firstName: u.firstName, lastName: u.lastName, email: u.email })),
    [pypUsersRes],
  );

  const filteredRegions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return regions;
    return regions.filter((region) => {
      const rypName = region.ryp ? `${region.ryp.firstName} ${region.ryp.lastName}`.toLowerCase() : "";
      const provinceNames = (region.provinces ?? []).map((p) => p.name.toLowerCase()).join(" ");
      return (
        region.name.toLowerCase().includes(q) ||
        region.address.toLowerCase().includes(q) ||
        rypName.includes(q) ||
        provinceNames.includes(q)
      );
    });
  }, [regions, search]);

  const toggleExpand = (id: string) => {
    setExpandedRegions((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleCreateRegion = async (values: RegionFormState) => {
    setCreateError(null);
    try {
      await createRegion(values as any).unwrap();
      setShowCreate(false);
    } catch (err: any) {
      setCreateError(err?.data?.error ?? err?.data?.message ?? "Could not create region.");
    }
  };

  const handleAssignRyp = async (regionId: string, rypId: string) => {
    setAssignError(null);
    try {
      await updateRegion({ id: regionId, ryp: rypId }).unwrap();
      setAssignModal({ open: false, regionId: null, regionName: "", currentRypId: undefined });
    } catch (err: any) {
      setAssignError(err?.data?.error ?? err?.data?.message ?? "Could not assign RYP.");
    }
  };

  const handleArchiveToggle = async (regionId: string, archived: boolean) => {
    try {
      if (archived) await unarchiveRegion(regionId).unwrap();
      else await archiveRegion(regionId).unwrap();
    } catch { /* keep quiet */ }
  };

  if (!hasAccess) {
    return (
      <div className="space-y-6 pb-12">
        <div>
          <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-purple-600">Territory Management</p>
          <h1 className="text-2xl font-bold text-gray-900">Regions</h1>
        </div>
        <div className="flex flex-col items-center justify-center rounded-2xl border border-red-100 bg-red-50 px-6 py-20 text-center">
          <ShieldAlert size={28} className="mb-3 text-red-400" />
          <p className="text-sm font-semibold text-red-700">Access denied</p>
          <p className="mt-1 max-w-md text-xs text-red-500">
            Only <strong>cyp</strong>, <strong>admin</strong>, <strong>co</strong>, and <strong>committee_chair</strong> can manage regions.
          </p>
        </div>
      </div>
    );
  }

  const summary = {
    total: regions.length,
    withRyp: regions.filter((r) => !!r.ryp).length,
    archived: regions.filter((r) => r.isArchived).length,
    active: regions.filter((r) => !r.isArchived).length,
    provinces: regions.reduce((acc, r) => acc + (r.provinces?.filter((p) => !p.isArchived).length ?? 0), 0),
  };

  return (
    <div className="space-y-6 pb-12">
      {/* HEADER */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-purple-600">Territory Management</p>
          <h1 className="text-2xl font-bold text-gray-900">Regions & Provinces</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create regions, add provinces, assign RYPs and PYPs, and archive inactive territories.
          </p>
        </div>
        <button
          onClick={() => { setCreateError(null); setShowCreate(true); }}
          className="inline-flex items-center gap-2 rounded-xl bg-purple-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-purple-700"
        >
          <Plus size={16} />
          Add region
        </button>
      </div>

      {/* SUMMARY */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
        {[
          { label: "Regions", value: summary.total, icon: <MapPin size={13} className="text-purple-400" />, color: "text-purple-700" },
          { label: "Active", value: summary.active, icon: <Building2 size={13} className="text-green-400" />, color: "text-green-700" },
          { label: "With RYP", value: summary.withRyp, icon: <Users size={13} className="text-blue-400" />, color: "text-blue-700" },
          { label: "Provinces", value: summary.provinces, icon: <Building2 size={13} className="text-indigo-400" />, color: "text-indigo-700" },
          { label: "Archived", value: summary.archived, icon: <Archive size={13} className="text-amber-400" />, color: "text-amber-700" },
        ].map((item) => (
          <div key={item.label} className="rounded-xl border border-gray-100 bg-white px-4 py-4 shadow-sm">
            <div className="mb-1 flex items-center gap-1.5 text-[11px] text-gray-400">
              {item.icon}
              {item.label}
            </div>
            {regionsLoading ? (
              <Bone className="mt-1 h-7 w-14" />
            ) : (
              <p className={`text-2xl font-bold ${item.color}`}>{item.value}</p>
            )}
          </div>
        ))}
      </div>

      {/* FILTERS */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search by region, province, address, or RYP..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex items-center gap-1 rounded-xl bg-gray-100 p-1">
          <button
            onClick={() => setShowArchived(false)}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${!showArchived ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
          >
            Active
          </button>
          <button
            onClick={() => setShowArchived(true)}
            className={`rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${showArchived ? "bg-white text-gray-900 shadow-sm" : "text-gray-500"}`}
          >
            Archived
          </button>
        </div>
      </div>

      {/* LIST */}
      {regionsLoading ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {[0, 1, 2, 3].map((i) => <Bone key={i} className="h-56" />)}
        </div>
      ) : filteredRegions.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-gray-100 bg-white px-6 py-20 text-center shadow-sm">
          <MapPin size={28} className="mb-3 text-gray-300" />
          <p className="text-sm font-semibold text-gray-600">
            {search ? "No regions match your search" : showArchived ? "No archived regions" : "No regions yet"}
          </p>
          <p className="mt-1 max-w-sm text-xs text-gray-400">
            {showArchived ? "Archived regions will appear here." : 'Create your first region using the "Add region" button.'}
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 text-[11px] text-gray-400">
            {regionsFetching && <Loader2 size={12} className="animate-spin" />}
            <span>{filteredRegions.length} region{filteredRegions.length !== 1 ? "s" : ""}</span>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {filteredRegions.map((region) => {
              const currentRypId = region.ryp?.id ?? (region.ryp as any)?._id;
              const isExpanded = expandedRegions.has(region.id);
              const activeProvinceCount = (region.provinces ?? []).filter((p) => !p.isArchived).length;

              return (
                <div
                  key={region.id}
                  className={`overflow-hidden rounded-2xl border bg-white shadow-sm transition-all hover:shadow-md ${
                    region.isArchived ? "border-dashed border-amber-200" : "border-gray-100"
                  }`}
                >
                  <div className={`h-1 w-full ${region.isArchived ? "bg-amber-300" : "bg-purple-400"}`} />

                  <div className="space-y-4 p-5">
                    {/* Region header */}
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="mb-1 flex items-center gap-2 flex-wrap">
                          <h3 className="truncate text-base font-semibold text-gray-900">{region.name}</h3>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${region.isArchived ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>
                            {region.isArchived ? "Archived" : "Active"}
                          </span>
                          <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[10px] font-bold text-indigo-600">
                            {activeProvinceCount} province{activeProvinceCount !== 1 ? "s" : ""}
                          </span>
                        </div>
                        <p className="text-sm leading-relaxed text-gray-500">{region.address}</p>
                      </div>
                    </div>

                    {/* RYP */}
                    <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                      <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.12em] text-gray-400">
                        Assigned RYP
                      </p>

                      {region.ryp ? (
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {region.ryp.firstName} {region.ryp.lastName}
                            </p>
                            <p className="text-[11px] text-gray-500">{region.ryp.email}</p>
                          </div>
                          <button
                            onClick={() => { setAssignError(null); setAssignModal({ open: true, regionId: region.id, regionName: region.name, currentRypId }); }}
                            className="rounded-lg border border-gray-200 px-3 py-1.5 text-[11px] font-semibold text-purple-600 hover:bg-white"
                          >
                            Change RYP
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between gap-3">
                          <p className="text-xs text-gray-400">No RYP assigned yet.</p>
                          <button
                            onClick={() => { setAssignError(null); setAssignModal({ open: true, regionId: region.id, regionName: region.name, currentRypId: undefined }); }}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-purple-700"
                          >
                            <UserPlus size={12} />
                            Assign RYP
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Provinces expandable section */}
                    <div>
                      <button
                        onClick={() => toggleExpand(region.id)}
                        className="w-full flex items-center justify-between rounded-xl border border-gray-100 px-4 py-2.5 text-xs font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          <Building2 size={13} className="text-purple-400" />
                          Manage provinces
                        </div>
                        {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>

                      {isExpanded && (
                        <div className="mt-3">
                          <ProvincePanel region={region} pypOptions={pypOptions} />
                        </div>
                      )}
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-between gap-3 border-t border-gray-100 pt-3">
                      <div className="text-[11px] text-gray-400">
                        Created {new Date(region.createdAt).toLocaleDateString()}
                      </div>
                      <button
                        onClick={() => handleArchiveToggle(region.id, region.isArchived)}
                        disabled={isArchiving || isUnarchiving}
                        className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
                          region.isArchived
                            ? "bg-green-50 text-green-700 hover:bg-green-100"
                            : "bg-amber-50 text-amber-700 hover:bg-amber-100"
                        } disabled:opacity-50`}
                      >
                        {region.isArchived ? <><RotateCcw size={12} /> Restore</> : <><Archive size={12} /> Archive</>}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <CreateRegionModal
        isOpen={showCreate}
        onClose={() => setShowCreate(false)}
        onSubmit={handleCreateRegion}
        isSaving={isCreating}
        apiError={createError}
      />

      <AssignRypModal
        isOpen={assignModal.open}
        onClose={() => setAssignModal({ open: false, regionId: null, regionName: "", currentRypId: undefined })}
        regionId={assignModal.regionId}
        regionName={assignModal.regionName}
        currentRypId={assignModal.currentRypId}
        rypOptions={rypOptions}
        onSubmit={handleAssignRyp}
        isSaving={isUpdatingRegion}
        apiError={assignError}
      />

      {rypLoading && <div className="text-[11px] text-gray-400">Loading RYP users...</div>}
    </div>
  );
};

export default AdminRegion;